import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { LoadingState } from "@repo/app-types/client/client";
import type { PaymentMethodInfo } from "@repo/app-types/entities/balanceTransaction";
import type { BillingProductInfo } from "@repo/app-types/entities/billing";
import { getAxiosInstanceWithAuth } from "../../api/axiosConfig.js";
import type { RootState } from "../../app/store.js";

export interface BillingState {
  billing: BillingInfo;
  giftCardBalance: GiftCardBalanceInfo;
}
export interface BillingInfo {
  defaultPaymentMethod: PaymentMethodInfo | null;
  planInfo: BillingProductInfo | null;
  status: LoadingState;
  error: string | null;
}

export interface GiftCardBalanceInfo {
  balanceUnitAmount: number | null;
  pendingBalanceUnitAmount: number | null;
  status: LoadingState;
  error: string | null;
}

const initialState: BillingState = {
  billing: {
    defaultPaymentMethod: null,
    planInfo: null,
    status: LoadingState.LOADING,
    error: null,
  },
  giftCardBalance: {
    // balances in cents
    balanceUnitAmount: null,
    pendingBalanceUnitAmount: null,
    status: LoadingState.LOADING,
    error: null,
  },
};

export const fetchBillingInfo = createAsyncThunk(
  "billing/fetch",
  async (_args, thunkAPI) => {
    try {
      const axios = await getAxiosInstanceWithAuth();
      const { data } = await axios.get(`/billing/info`);

      return data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const fetchGiftCardBalance = createAsyncThunk(
  "billing/fetchBalance",
  async (_args, thunkAPI) => {
    try {
      const axios = await getAxiosInstanceWithAuth();
      const { data } = await axios.post(`/billing/check-balance`);

      return data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

// Slice
const billingSlice = createSlice({
  name: "billing",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBillingInfo.pending, (state) => {
        state.billing.status = LoadingState.LOADING;
      })
      .addCase(fetchBillingInfo.fulfilled, (state, action) => {
        state.billing.status = LoadingState.LOADED;
        state.billing.planInfo = action.payload.platform;
        state.billing.defaultPaymentMethod =
          action.payload.default_payment_method;
      })
      .addCase(fetchBillingInfo.rejected, (state, action) => {
        state.billing.status = LoadingState.ERROR;
        state.billing.error = action.error.message || null;
      })
      .addCase(fetchGiftCardBalance.pending, (state) => {
        state.giftCardBalance.status = LoadingState.LOADING;
      })
      .addCase(fetchGiftCardBalance.fulfilled, (state, action) => {
        state.giftCardBalance.status = LoadingState.LOADED;
        state.giftCardBalance.balanceUnitAmount =
          action.payload.balance_unit_amount;
        state.giftCardBalance.pendingBalanceUnitAmount =
          action.payload.pending_unit_amount;
      })
      .addCase(fetchGiftCardBalance.rejected, (state, action) => {
        state.giftCardBalance.status = LoadingState.ERROR;
        state.giftCardBalance.error = action.error.message || null;
      });
  },
});

export default billingSlice.reducer;

export const selectBillingInfo = (state: RootState) => state.billing.billing;
export const selectDefaultPaymentMethod = (state: RootState) =>
  state.billing.billing.defaultPaymentMethod;
export const selectPlanInfo = (state: RootState) =>
  state.billing.billing.planInfo;
export const selectBillingLoadingState = (state: RootState) =>
  state.billing.billing.status;
export const selectBalanceInfo = (state: RootState) =>
  state.billing.giftCardBalance;
